import { Injectable, inject } from '@angular/core';
import { Deposits } from '@freelancer/deposits';
import {
  PaymentsErrorType,
  PaymentsMessagingService,
  PaymentsProcessError,
  PaymentsResultStatus,
} from '@freelancer/payments-messaging';
import { PaymentsTrackingService } from '@freelancer/payments-tracking';
import { Pwa } from '@freelancer/pwa';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DepositMethodApi } from 'api-typings/common/common';
import type {
  DepositRequestApi,
  DepositRequestConfirmationApi,
} from 'api-typings/payments/payments';
import { firstValueFrom } from 'rxjs';
import { GOOGLE_PAY_CONFIG } from './google-pay.config';
import type { GooglePayConfig } from './google-pay.interface';
import type { GooglePayDepositRequest } from './google-pay.model';

const GOOGLE_PAY_SCRIPT_URL = 'https://pay.google.com/gp/p/js/pay.js';
const GOOGLE_PAY_API_VERSION = 2;
const GOOGLE_PAY_API_VERSION_MINOR = 0;

/**
 * Exclude Elo/Interac because we lack Brazilian and Canadian merchant account
 * respectively to process transactions with those card networks.
 */
const ALLOWED_CARD_NETWORKS: google.payments.api.CardNetwork[] = [
  'AMEX',
  'DISCOVER',
  'MASTERCARD',
  'VISA',
];

const ALLOWED_CARD_AUTH_METHODS: google.payments.api.CardAuthMethod[] = [
  'PAN_ONLY',
  'CRYPTOGRAM_3DS',
];

/**
 * Service to handle Google Pay payments based on the Google Pay JS API
 */
@UntilDestroy({ className: 'GooglePay' })
@Injectable({
  providedIn: 'root',
})
export class GooglePay {
  private pwa = inject(Pwa);
  private deposits = inject(Deposits);
  private googlePayConfig = inject<GooglePayConfig>(GOOGLE_PAY_CONFIG);
  private paymentsMessaging = inject(PaymentsMessagingService);
  private paymentsTracking = inject(PaymentsTrackingService);

  private scriptPromise?: Promise<void>;
  private paymentsClient?: google.payments.api.PaymentsClient;

  /**
   * Injects the Google Pay JS API script if not already loading or loaded.
   */
  private loadScript(): Promise<void> {
    if (this.scriptPromise) {
      return this.scriptPromise;
    }

    this.scriptPromise = new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = GOOGLE_PAY_SCRIPT_URL;
      script.async = true;

      const onScriptLoad = (): void => {
        cleanup();
        resolve();
      };

      const onScriptError = (): void => {
        cleanup();
        this.scriptPromise = undefined;
        script.remove();
        reject(new Error(`Unable to load script ${GOOGLE_PAY_SCRIPT_URL}`));
      };

      script.addEventListener('load', onScriptLoad);
      script.addEventListener('error', onScriptError);
      document.body.appendChild(script);

      function cleanup(): void {
        script.removeEventListener('load', onScriptLoad);
        script.removeEventListener('error', onScriptError);
      }
    });

    return this.scriptPromise;
  }

  /**
   * Load the Google Pay API and check if it is supported.
   */
  async isSupported(): Promise<boolean> {
    // T338341 - Android integration for Google Pay API has not been approved
    // T339427 - iOS needs the WKWebView integration before Google Pay can work
    const platform = this.pwa.getPlatform();
    if (platform === 'android' || platform === 'ios') {
      return false;
    }

    try {
      await this.loadScript();
    } catch {
      return false;
    }
    return !!window.google?.payments?.api;
  }

  /**
   * Load Google Pay payments client if it is not already loaded and returns it.
   * Returns null if it can not be loaded.
   */
  async loadGooglePayPaymentsClient(): Promise<google.payments.api.PaymentsClient | null> {
    if (this.paymentsClient) {
      return this.paymentsClient;
    }

    if (!(await this.isSupported())) {
      return null;
    }

    try {
      this.paymentsClient = new window.google.payments.api.PaymentsClient({
        environment: this.googlePayConfig.environment,
      });
    } catch {
      return null;
    }
    return this.paymentsClient;
  }

  /**
   * Check whether user has one or more cards that we support in Google Pay.
   */
  async isReadyToPay(): Promise<google.payments.api.IsReadyToPayResponse | null> {
    const paymentsClient = await this.loadGooglePayPaymentsClient();
    if (!paymentsClient) {
      return null;
    }

    try {
      const response = await paymentsClient.isReadyToPay(
        this.getIsReadyToPayRequest(),
      );
      return response;
    } catch {
      return null;
    }
  }

  /**
   * Open the Google Pay payment sheet for the given deposit request.
   */
  async makePayment(request: GooglePayDepositRequest): Promise<void> {
    const paymentsClient = await this.loadGooglePayPaymentsClient();
    if (!paymentsClient) {
      this.paymentsMessaging.pushError({
        errorType: PaymentsErrorType.ERROR_UNKNOWN,
        errorMessage: 'Failed to load Google Pay payments client.',
      });
      return;
    }

    const payload: DepositRequestApi = {
      amount: request.depositAmount,
      currency_id: request.paymentCurrency.id,
      country_code: request.countryCode,
      deposit_method: DepositMethodApi.GOOGLEPAY,
      user_id: request.userId,
      cart_id: request.cartId,
      threatmetrix_session: request.threatmetrixSession,
      back_url: request.backUrl,
      tracking_token: request.trackingToken,
      cancel_url: request.cancelUrl,
      action: request.action,
      domain: request.domain,
    };
    const response = await firstValueFrom(
      this.deposits.deposit(payload).pipe(untilDestroyed(this)),
    );

    const basePaymentsProcessError: PaymentsProcessError = {
      errorType: PaymentsErrorType.ERROR,
      eventId: response.requestId,
    };
    if (response.status !== 'success') {
      this.paymentsMessaging.pushError({
        ...basePaymentsProcessError,
        errorCode: response.errorCode,
      });
      return;
    }

    const invoiceId = response.result.invoice?.id;
    const invoiceAmount = response.result.invoice?.amount;
    if (!invoiceId || invoiceAmount === undefined) {
      this.paymentsMessaging.pushError({
        errorType: PaymentsErrorType.ERROR,
        eventId: response.requestId,
      });
      return;
    }

    let paymentData: google.payments.api.PaymentData;
    try {
      paymentData = await paymentsClient.loadPaymentData(
        this.getPaymentDataRequest(request, invoiceAmount),
      );
    } catch (error) {
      const paymentsError = error as google.payments.api.PaymentsError;
      this.paymentsTracking.pushTrackingData({
        label: `googlePayPaymentSheet-${paymentsError.statusCode}`,
        name: DepositMethodApi.GOOGLEPAY,
        reference: 'invoice_id',
        referenceId: invoiceId,
      });
      switch (paymentsError.statusCode) {
        case 'CANCELED':
          this.paymentsMessaging.pushMessage({
            paymentsStatus: PaymentsResultStatus.INTERRUPTED,
            method: DepositMethodApi.GOOGLEPAY,
          });
          break;
        default:
          this.paymentsMessaging.pushError({
            ...basePaymentsProcessError,
            errorCode: paymentsError.statusCode,
            errorMessage: paymentsError.statusMessage,
          });
      }
      return;
    }

    const confirmPayload: DepositRequestConfirmationApi = {
      user_id: request.userId,
      deposit_method: DepositMethodApi.GOOGLEPAY,
      /** Field reused for one-time wallet token from Google Pay API. */
      paypal_token: paymentData.paymentMethodData.tokenizationData.token,
      invoice_id: invoiceId,
      action: request.action,
      tracking_token: request.trackingToken,
      threeds_browser_info: request.threedsBrowserInfo,
    };
    const confirmResponse = await this.deposits.depositUpdate(confirmPayload);

    if (confirmResponse.status !== 'success') {
      this.paymentsMessaging.pushError({
        ...basePaymentsProcessError,
        errorCode: confirmResponse.errorCode,
      });
      return;
    }

    if (confirmResponse.result.is_threeds_triggered) {
      await this.deposits.handleDepositConfirmThreeDS(
        confirmResponse,
        confirmPayload,
      );
      return;
    }

    this.paymentsMessaging.pushMessage({
      paymentsStatus: PaymentsResultStatus.SUCCESS,
      method: DepositMethodApi.GOOGLEPAY,
    });
  }

  private getAllowedCardPaymentMethod(): google.payments.api.PaymentMethodSpecification {
    return {
      type: 'CARD',
      parameters: {
        allowedAuthMethods: ALLOWED_CARD_AUTH_METHODS,
        allowedCardNetworks: ALLOWED_CARD_NETWORKS,
      },
      tokenizationSpecification: {
        type: 'PAYMENT_GATEWAY',
        parameters: {
          gateway: this.googlePayConfig.gateway,
          gatewayMerchantId: this.googlePayConfig.gatewayMerchantId,
        },
      },
    };
  }

  private getIsReadyToPayRequest(): google.payments.api.IsReadyToPayRequest {
    return {
      apiVersion: GOOGLE_PAY_API_VERSION,
      apiVersionMinor: GOOGLE_PAY_API_VERSION_MINOR,
      allowedPaymentMethods: [this.getAllowedCardPaymentMethod()],
      existingPaymentMethodRequired: true,
    };
  }

  private getPaymentDataRequest(
    request: GooglePayDepositRequest,
    totalPrice: number,
  ): google.payments.api.PaymentDataRequest {
    const merchantInfo: google.payments.api.MerchantInfo = {
      merchantName: this.googlePayConfig.merchantName,
      merchantId: this.googlePayConfig.merchantId,
    };

    return {
      apiVersion: GOOGLE_PAY_API_VERSION,
      apiVersionMinor: GOOGLE_PAY_API_VERSION_MINOR,
      allowedPaymentMethods: [this.getAllowedCardPaymentMethod()],
      transactionInfo: {
        countryCode: request.countryCode,
        currencyCode: request.paymentCurrency.code,
        totalPriceStatus: 'FINAL',
        totalPrice: totalPrice.toFixed(2),
      },
      merchantInfo,
    };
  }
}
